import { useCallback, useEffect, useState } from 'react'

import type { DayV2 } from '@library/api-deliveries'
import { fetchV2DaysForUser } from '@library/api-deliveries'
import { fetchUserOrders } from '@library/api-user'
import { BROWSER_ERROR_KEYS, instrumentBrowserError } from '@library/observability/browser'

type Props = {
  slotId: string
  cutOffDateTimeFrom: string
  cutOffDateTimeUntil: string
  postcode: string
  selectedDate?: string
}

export type DeliveryDays = {
  data: DayV2[] | null
  deliveryDate: string | null
  dsltId: string | null
}

/**
 * Loading, Error State, Delivery Day
 */
type UseSubscriptionToDeliveryDays = [boolean, Error | null, DeliveryDays]

export function useSubscriptionToDeliveryDays({
  slotId,
  cutOffDateTimeFrom,
  cutOffDateTimeUntil,
  postcode,
  selectedDate,
}: Props): UseSubscriptionToDeliveryDays {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [returnedDeliveryDay, setDeliveryDay] = useState<DeliveryDays>({
    data: null,
    deliveryDate: null,
    dsltId: null,
  })

  const tryFetchDays = useCallback(async () => {
    try {
      const data = await fetchV2DaysForUser({
        filters: {
          cutoff_datetime_from: cutOffDateTimeFrom,
          cutoff_datetime_until: cutOffDateTimeUntil,
        },
        sort: 'date',
        direction: 'asc',
        postcode,
        order_type: 'resubscription',
      })

      const userOrders = await fetchUserOrders()

      const excludedDeliveryDates = new Set(
        userOrders.map((order) => {
          if (order.deliveryDate) return order.deliveryDate.slice(0, 10)
          else {
            return null
          }
        }),
      )

      const filteredDays = data.filter((day) => !excludedDeliveryDates.has(day.date))

      const defaultDeliveryDays = filteredDays.map((day) => ({
        ...day,
        daySlotLeadTimes: [day.daySlotLeadTimes[0]],
      }))

      const deliverySlotMatchedWithSubscriptionSlotId = defaultDeliveryDays
        .flatMap((day) => day.daySlotLeadTimes)
        .find((dslt) => {
          // slotId is not unique to one DSLT, it is shared among multiple DSLTs
          // in the case of a date is not selected, we will default to the nearest delivery date
          // otherwise, we will match the slotId and date
          if (!selectedDate) {
            return dslt.slotId === slotId
          }

          return dslt.slotId === slotId && dslt.date === selectedDate
        })

      setDeliveryDay({
        data: defaultDeliveryDays,
        deliveryDate: deliverySlotMatchedWithSubscriptionSlotId?.date ?? null,
        dsltId: deliverySlotMatchedWithSubscriptionSlotId?.id ?? null,
      })
    } catch (err: unknown) {
      setError(err as Error)
      instrumentBrowserError(BROWSER_ERROR_KEYS.SUBSCRIPTION_TO_DELIVERY_DAYS_ERROR, { error: err })
    } finally {
      setLoading(false)
    }
  }, [cutOffDateTimeFrom, cutOffDateTimeUntil, postcode, selectedDate, slotId])

  useEffect(() => {
    tryFetchDays()
  }, [tryFetchDays])

  return [loading, error, returnedDeliveryDay]
}
